import { WizardView, Gap, P } from '@salecycle/ui-component-library';
import Spinner from 'core/ui/components/spinner';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Route, Switch, useHistory } from 'react-router-dom';
import { Actions as RouterActions, Selectors as RouterSelectors } from 'router';
import { State } from 'store';
import { useNewSftpUserDispatch, useNewSftpUserState } from '../context/useNewSftpUserContext';
import { getSteps, stepRoutes } from '../data/NewSftpUserConstants';
import { arePreviousStepsValid, buildNewSftpUserPath, redirectToLastValidStep } from '../utils/helpers';
import AddSftpUserStep from './steps/AddNewSftpUserStep';
import SummaryModal from './steps/SummaryStep/SummaryModal';
import SummaryStep from './steps/SummaryStep/SummaryStep';
import { UnsavedChangesModal } from 'core/modals/UnsavedChangesModal';
import { NewSftpUserSuccessPage } from './NewSftpUserSuccessPage';
import { ISftpUserData } from 'sections/user-management/entities';
import { useMutation } from 'react-query';
import * as RestApi from 'core/network/RestApi';
import { RestApiError } from 'core/network/RestApi';

interface IStateProps {
  loadingClientsData: boolean;
  currentStep?: number;
  pathname?: string;
}

interface DispatchProps {
  push: (href: string) => void;
}

export type IProps = IStateProps & DispatchProps;

/* eslint-disable react-hooks/exhaustive-deps */
export const NewSftpUserWizard = ({ loadingClientsData, currentStep, pathname, push }: IProps) => {
  const { t } = useTranslation('sftpUserManagement');
  const history = useHistory();
  const [summaryModalIsOpen, setSummaryModalIsOpen] = useState(false);
  const [unsavedChangesModalIsOpen, setUnsavedChangesModalIsOpen] = useState(false);
  const { sftpUser, success, error, errorMessage, stepValid, validSteps } = useNewSftpUserState();
  const dispatch = useNewSftpUserDispatch();

  const steps = getSteps(t);

  useEffect(() => () => dispatch({ type: 'RESET_WIZARD' }), []);

  useEffect(() => {
    const currentPath = pathname.replace(buildNewSftpUserPath(''), '');
    let validPath = false;

    steps.forEach(step => {
      if (step.route === currentPath) {
        validPath = true;
      }
    });

    if (!validPath) {
      push(buildNewSftpUserPath(steps[0].route));
    }
  }, [pathname]);

  useEffect(() => {
    const prevStepsValid = arePreviousStepsValid(validSteps, currentStep);

    if (!prevStepsValid) {
      redirectToLastValidStep(steps, validSteps, push);
    }
  }, [currentStep]);

  useEffect(() => {
    if (error) {
      setSummaryModalIsOpen(false);
    }
  }, [error]);

  const exitWizard = () => {
    setUnsavedChangesModalIsOpen(false);
    history.push('/user-management/sftp-users');
  };

  const onExitButtonClicked = () => {
    setUnsavedChangesModalIsOpen(true);
  };

  const onPrevButtonClicked = () => {
    if (currentStep === 1) {
      setUnsavedChangesModalIsOpen(true);
    } else {
      push(buildNewSftpUserPath(steps[currentStep - 2].route));
    }
  };

  const onNextButtonClicked = () => {
    if (currentStep !== steps.length) {
      push(buildNewSftpUserPath(steps[currentStep].route));
    } else {
      setSummaryModalIsOpen(true);
    }
  };

  const closeSummaryModal = () => {
    setSummaryModalIsOpen(false);
  };

  const postNewSftpUser = useMutation((sftpUser: ISftpUserData) =>
    RestApi.post<{ username: string }, ISftpUserData>(`/public_sftp_users/`, sftpUser),
  );

  const handleSummaryModalSubmit = async () => {
    const newSftpUser: ISftpUserData = {
      email: sftpUser.email,
      username: sftpUser.username,
      enabled: true,
      sites: sftpUser.sites ? sftpUser.sites.map(s => s.value).join(',') : '',
      brands: sftpUser.brands ? sftpUser.brands.map(s => s.value).join(',') : '',
      groups: sftpUser.groups ? sftpUser.groups.map(s => s.value).join(',') : '',
      userType: sftpUser.userType,
    };
    postNewSftpUser.mutate(newSftpUser, {
      onSuccess: data => {
        dispatch({
          type: 'CREATE_SFTP_USER_SUCCESS',
          payload: { createdSftpUser: data.data.username, success: true, error: false, errorMessage: '' },
        });
      },
      onError: (error: RestApiError, variables, context) => {
        dispatch({ type: 'CLEAR_ERROR' });
        dispatch({
          type: 'CREATE_SFTP_USER_FAIL',
          payload: { createdSftpUser: null, success: false, error: true, errorMessage: error.message },
        });
      },
    });
  };

  if (loadingClientsData) {
    return <Spinner />;
  }

  if (success) {
    return <NewSftpUserSuccessPage />;
  } else {
    return (
      <WizardView
        currentStep={currentStep}
        steps={steps.map(step => {
          return { label: step.label };
        })}
        onExitButtonClicked={currentStep !== 1 ? onExitButtonClicked : null}
        onPrevButtonClicked={onPrevButtonClicked}
        onNextButtonClicked={onNextButtonClicked}
        exitButtonLabel={t('newSftpUser.wizard.cancel')}
        prevButtonLabel={currentStep === 1 ? t('newSftpUser.wizard.exit') : t('newSftpUser.wizard.back')}
        nextButtonLabel={currentStep !== steps.length ? t('newSftpUser.wizard.next') : t('newSftpUser.wizard.confirm')}
        nextButtonDisabled={!stepValid}
      >
        <UnsavedChangesModal
          heading={t('newSftpUser.unsavedChangesModal.heading')}
          isOpen={unsavedChangesModalIsOpen}
          onOkButtonClicked={exitWizard}
          onCancelButtonClicked={() => setUnsavedChangesModalIsOpen(false)}
        >
          <P>{t('newSftpUser.unsavedChangesModal.text1')}</P>
          <Gap top={10} />
          <P>{t('newSftpUser.unsavedChangesModal.text2')}</P>
        </UnsavedChangesModal>
        <Switch>
          <Route
            exact={true}
            path={`/user-management/sftp-users/new-sftp-user${steps[0].route}`}
            render={() => currentStep === 1 && <AddSftpUserStep currentStep={currentStep} />}
          />
          <Route
            exact={true}
            path={`/user-management/sftp-users/new-sftp-user${steps[1].route}`}
            render={() =>
              currentStep === 2 &&
              arePreviousStepsValid(validSteps, currentStep) && (
                <>
                  <SummaryModal
                    isOpen={summaryModalIsOpen}
                    heading={t('newSftpUser.summaryStep.modal.heading')}
                    onOkButtonClicked={handleSummaryModalSubmit}
                    onCloseButtonClicked={closeSummaryModal}
                    onCancelButtonClicked={closeSummaryModal}
                    okButtonLabel={t('newSftpUser.summaryStep.modal.ok')}
                    cancelButtonLabel={t('newSftpUser.summaryStep.modal.cancel')}
                  />
                  <SummaryStep push={push} currentStep={currentStep} isError={error} errorMessage={`${errorMessage}`} />
                </>
              )
            }
          />
        </Switch>
      </WizardView>
    );
  }
};

const actionCreators: DispatchProps = {
  push: RouterActions.push,
};

const mapStateToProps = (state: State): IStateProps => {
  let loadingClientsData = true;

  if (
    state.clients.groups.data.allIds.length !== 0 &&
    state.clients.brands.data.allIds.length !== 0 &&
    state.clients.sites.data.allIds.length !== 0
  ) {
    loadingClientsData = false;
  }

  const { pathname } = state.router.location;
  const currentStepName = RouterSelectors.getCurrentSftpUserWizardStep(state);
  const currentStep = stepRoutes.indexOf(`/${currentStepName}`) + 1;

  return {
    loadingClientsData,
    currentStep,
    pathname,
  };
};

export default connect(mapStateToProps, actionCreators)(NewSftpUserWizard);
